import { Directive, ElementRef, Input } from '@angular/core';

@Directive({ selector: '[formattedContent]' })
export class FormattedContentDirective {

	private whitelist: IWhiteListTag[] = [
		{ tag: 'div', replaceWith: 'div' },
		{ tag: 'p', replaceWith: 'div' },
		{ tag: 'a', replaceWith: 'a', attributesToKeep: ['href', 'target'] },
		{ tag: 'span', replaceWith: 'span' },
		{ tag: 'br', replaceWith: 'br', noEndTag: true },
		{ tag: 'b', replaceWith: 'b' },
		{ tag: 'strong', replaceWith: 'b' },
		{ tag: 'em', replaceWith: 'i' },
		{ tag: 'i', replaceWith: 'i' },
		{ tag: 'u', replaceWith: 'u' },
		{ tag: 'li', replaceWith: 'li' },
		{ tag: 'ul', replaceWith: 'ul' },
		{ tag: 'ol', replaceWith: 'ol' },
		{ tag: 'hr', replaceWith: 'hr' },
		{ tag: 'h1', replaceWith: 'div' },
		{ tag: 'h2', replaceWith: 'div' },
		{ tag: 'h3', replaceWith: 'div' },
		{ tag: 'h4', replaceWith: 'div' },
		{ tag: 'h5', replaceWith: 'div' },
		{ tag: 'h6', replaceWith: 'div' }];

	private whitelistedTags = this.whitelist.map(item => item.tag)
		.sort((a, b) => { return b.length - a.length; });
	private whitelistedReplacementTags = [...new Set(this.whitelist.map(item => item.replaceWith))]
		.sort((a, b) => { return b.length - a.length; });

	private _isHtmlContent = false;
	private _content?: string;
	@Input() set isHtmlContent(value: boolean) {
		if (value === this.isHtmlContent) return;

		this._isHtmlContent = value;
		if (this._content) this.format();
	}
	get isHtmlContent(): boolean { return this._isHtmlContent;}

	@Input('formattedContent')
	set formattedContent(content: string) {
		this._content = content;
		this.format();
	}

	private format() {
		let content = this._content;

		if (!content) {
			this.element.nativeElement.innerHTML = '';
			return;
		}

		if (this.isHtmlContent) {
			content = this.formatHtmlContentToSafeCode(content);
			this.element.nativeElement.style.whiteSpace = 'normal';
		} else {
			content = content.replace(/\</g, '&lt;');
		}

		content = this.formatSafeCodeToHtml(content);
		content = this.formatLinks(content);

		this.element.nativeElement.innerHTML = content;
	}

	constructor(private element: ElementRef<HTMLElement>) {
	}

	formatLinks(content: string): string {
		const currentLinks: { text: string, link: string }[] = [];
		content = content.replace(/(<a.*?(href=(?:"|')(.*?)(?:"|').*?>)(.*?)<\/a>)/gim, (originalString: string, group1: string, group2: string, group3: string, group4: string) => {
			const numOfLink = currentLinks.length;
			currentLinks.push({ text: group4, link: group3 });
			return '##link' + numOfLink + '##';
		});

		content = content.replace(/((https?:\/\/|www\.)[^\s\<]+)/gim, (originalString: string, group1: string, group2: string) => {
			let prefix = '';
			if (group2 === 'www.') {
				prefix = 'http://';
			}
			return '<a href="' + prefix + group1 + '" target="_blank">' + group1 + '</a>';
		});

		content = content.replace(/##link(\d+)##/gim, (originalString: string, group1: string) => {
			const index: number = parseInt(group1);
			return '<a href="' + currentLinks[index].link + '" target="_blank">' + currentLinks[index].text + '</a>';
		});
		return content;
	}

	formatHtmlContentToSafeCode(content: string): string {

		// Get content of body if present
		const getHtmlRegex = /(<(html)[^>]*>)((.|\s)+?)<\/\2>/im;
		let htmlMatch = getHtmlRegex.exec(content);
		while (htmlMatch != null) {
			const getBodyRegex = /(<(body)[^>]*>)((.|\s)*)<\/\2>/gim;
			const groups = getBodyRegex.exec(htmlMatch[0]);
			content = content.replace(htmlMatch[0], groups ? groups[3] : '');
			htmlMatch = getHtmlRegex.exec(content);
		}

		// Remove non whitelisted tags but keep content
		const getContentFromDisallowedTags = new RegExp('(<(?!(?:' + this.whitelistedTags.join('|') + ')(?:\\s|>))([^\\/\\s>]+)([^>]*?)?>)', 'im');
		let disallowedMatch = getContentFromDisallowedTags.exec(content);
		while (disallowedMatch != null) {
			content = content.replace(disallowedMatch[0], '').replace('</' + disallowedMatch[2] + '>', '');
			disallowedMatch = getContentFromDisallowedTags.exec(content);
		}

		// Find whitelisted html tags and remove undesired attributes
		const replaceWhitelistedTags = new RegExp('(<(' + this.whitelistedTags.join('|') + ')(.*?)>)', 'im');
		let whitelistedMatch = replaceWhitelistedTags.exec(content);
		while (whitelistedMatch != null) {
			const tag = whitelistedMatch[2];
			if (tag) {
				const whitelistedTag = this.whitelist.find(l => l.tag == tag);
				if (whitelistedTag) {
					content = content.replace(whitelistedMatch[0], '[' + this.getTagWithApprovedAttributes(whitelistedTag, whitelistedMatch[3]) + ']');
					content = content.replace('</' + tag + '>', '[/' + whitelistedTag.replaceWith + ']');
				}
			}
			whitelistedMatch = replaceWhitelistedTags.exec(content);
		}

		return content;
	}

	formatSafeCodeToHtml(content: string): string {

		// Find replaced whitelisted tags and replace back to html tags
		const reverseReplacedWhitelistedTags = new RegExp('(\\[(' + this.whitelistedReplacementTags.join('|') + ')(.*?)\\])', 'im');
		let reversedWhitelistedMatch = reverseReplacedWhitelistedTags.exec(content);
		while (reversedWhitelistedMatch != null) {
			const tag = reversedWhitelistedMatch[2];
			if (tag) {
				const whitelistedTag = this.whitelist.find(l => l.tag == tag);
				if (whitelistedTag) {
					content = content.replace(reversedWhitelistedMatch[0], '<' + this.getTagWithApprovedAttributes(whitelistedTag, reversedWhitelistedMatch[3]) + '>');
					if (content.indexOf('[/' + tag + ']') != -1) {
						content = content.replace('[/' + tag + ']', '</' + tag + '>');
					} else if (!whitelistedTag.noEndTag) {
						content += '</' + tag + '>';
					}
				}
			}

			reversedWhitelistedMatch = reverseReplacedWhitelistedTags.exec(content);
		}

		return content;
	}

	private getTagWithApprovedAttributes(whitelistedTag: IWhiteListTag, attributes: string) {
		if (whitelistedTag.attributesToKeep && attributes) {
			const attributesList = attributes.trim().split(' ');
			let newTag = whitelistedTag.replaceWith;
			for (let attribute of attributesList) {
				const attributeName = attribute.indexOf('=') > 0
					? attribute.substr(0, attribute.indexOf('='))
					: attribute;
				if (whitelistedTag.attributesToKeep.some(a => a == attributeName))
					newTag += ' ' + attribute;
			}
			return newTag;
		} else {
			return whitelistedTag.replaceWith;
		}
	}
}

export interface IWhiteListTag {
	tag: string;
	replaceWith: string;
	attributesToKeep?: string[];
	noEndTag?: boolean;
}
